/**
 * Thin, typed client for the Django REST API.
 *
 * - Same-origin session auth: cookies travel automatically.
 * - Unsafe requests carry the CSRF token Django sets in `pcms_csrftoken`.
 * - Errors are normalised from the backend envelope:
 *   { detail, code, errors: { field: [messages] }, retry_after?, attempts_left? }
 */

const BASE = '/api/v1'
const CSRF_COOKIE = 'pcms_csrftoken'

export type Query = Record<string, string | number | boolean | null | undefined | Array<string | number>>

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly errors: Record<string, string[]>
  readonly retryAfter?: number
  readonly attemptsLeft?: number

  constructor(status: number, body: Partial<ApiErrorBody>) {
    super(body.detail || defaultMessage(status))
    this.name = 'ApiError'
    this.status = status
    this.code = body.code ?? 'error'
    this.errors = body.errors ?? {}
    this.retryAfter = body.retry_after
    this.attemptsLeft = body.attempts_left
  }

  fieldError(field: string): string | undefined {
    return this.errors[field]?.[0]
  }
}

interface ApiErrorBody {
  detail: string
  code: string
  errors: Record<string, string[]>
  retry_after?: number
  attempts_left?: number
}

function defaultMessage(status: number): string {
  if (status === 0) return 'Can’t reach the server. Check your connection and try again.'
  if (status === 403) return 'You don’t have permission to do that.'
  if (status === 404) return 'We couldn’t find what you were looking for.'
  if (status >= 500) return 'Something went wrong on our side. Please try again in a moment.'
  return 'The request could not be completed.'
}

function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null
}

let csrfRequest: Promise<void> | null = null
async function ensureCsrf(): Promise<string> {
  if (!readCookie(CSRF_COOKIE)) {
    csrfRequest ??= fetch(`${BASE}/auth/csrf/`, { credentials: 'same-origin' })
      .then(() => undefined)
      .finally(() => {
        csrfRequest = null
      })
    await csrfRequest
  }
  return readCookie(CSRF_COOKIE) ?? ''
}

export function buildUrl(path: string, query?: Query): string {
  // Accept both "/students/" and already-absolute "/api/v1/..." paths from the API.
  const full = path.startsWith('/api/') ? path : `${BASE}${path}`
  const url = new URL(full, window.location.origin)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue
      if (Array.isArray(value)) value.forEach((v) => url.searchParams.append(key, String(v)))
      else url.searchParams.set(key, String(value))
    }
  }
  return url.pathname + url.search
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Query
  signal?: AbortSignal
}

export async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = { Accept: 'application/json' }
  let body: BodyInit | undefined

  if (method !== 'GET') headers['X-CSRFToken'] = await ensureCsrf()
  if (options.body instanceof FormData) {
    body = options.body
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.body)
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body,
      credentials: 'same-origin',
      signal: options.signal,
    })
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error
    throw new ApiError(0, {})
  }

  if (!response.ok) {
    let payload: Partial<ApiErrorBody> = {}
    try {
      payload = await response.json()
    } catch {
      /* non-JSON error page */
    }
    if (response.status === 401) window.dispatchEvent(new CustomEvent('pcms:unauthorized'))
    throw new ApiError(response.status, payload)
  }
  return response
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await request(path, options)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/** Download a file from the API (CSV export, resume) and hand it to the browser. */
export async function download(path: string, query?: Query, fallbackName = 'download'): Promise<void> {
  const response = await request(path, { query })
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  const name = utf?.[1] ? decodeURIComponent(utf[1]) : (plain?.[1] ?? fallbackName)
  const blob = await response.blob()
  const href = URL.createObjectURL(blob)
  const anchor = Object.assign(document.createElement('a'), { href, download: name })
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(href), 1000)
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Something went wrong.'
}
